import React, { useState } from 'react';
import { Upload, X, FileCheck, AlertCircle } from 'lucide-react';
import { DocumentType, uploadDocument } from '../utils/documentUpload';

interface DocumentUploadProps {
  documentType: DocumentType;
  studentId: string;
  onUploadComplete: (url: string) => void;
  onError: (error: string) => void;
  label: string;
  description?: string;
  required?: boolean;
  accept?: string;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  documentType,
  studentId,
  onUploadComplete,
  onError,
  label,
  description,
  required = true,
  accept = '.pdf,.jpg,.jpeg,.png'
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const url = await uploadDocument(file, documentType, studentId, description);
      setUploadedUrl(url);
      onUploadComplete(url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <input
          type="file"
          onChange={handleFileChange}
          accept={accept}
          disabled={uploading || !!uploadedUrl}
          className="hidden"
          id={`file-${documentType}`}
        />
        
        {!uploadedUrl ? (
          <label
            htmlFor={`file-${documentType}`}
            className={`flex items-center justify-center px-6 py-4 border-2 border-dashed rounded-lg cursor-pointer ${
              uploading
                ? 'bg-gray-50 border-gray-300'
                : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
            }`}
          >
            {uploading ? (
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500" />
                <span>Uploading...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-gray-600">
                <Upload className="h-5 w-5" />
                <span>Choose file or drag and drop</span>
              </div>
            )}
          </label>
        ) : (
          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2 text-green-700">
              <FileCheck className="h-5 w-5" />
              <span>Document uploaded successfully</span>
            </div>
            <button
              onClick={() => {
                setUploadedUrl(null);
                setError(null);
              }}
              className="text-green-700 hover:text-green-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center space-x-2 text-red-600 text-sm">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {description && !error && (
        <p className="text-sm text-gray-500">{description}</p>
      )}
    </div>
  );
};

export default DocumentUpload;